import {
  obfuscateCoordinates,
  removePII,
  getAnonymousSessionId,
  runPDPACheck
} from './privacy.js';

/**
 * Analytics 类（单例）
 * 负责用户行为数据收集、隐私合规检查、第三方分析服务集成
 *
 * 设计原则：
 * 1. 隐私优先：默认不收集任何数据，需用户明确同意
 * 2. 数据最小化：不发送精确坐标，仅区域级别信息
 * 3. PDPA 合规：所有数据收集前进行合规检查
 */
class Analytics {
  constructor() {
    this.enabled = false;
    this.consentGiven = false;
    this.scriptLoaded = false;
    this.sessionId = null;
    this._lastMapClick = null;
  }

  /**
   * 初始化 Analytics 模块
   * 检查用户是否已同意数据收集，同意后才加载分析脚本
   */
  init() {
    this.consentGiven = localStorage.getItem('fsm_analytics_consent') === 'true';
    this.enabled = this.consentGiven;

    if (this.enabled) {
      this._initializeSession();
      this._loadAnalyticsScript();
    }
  }

  /**
   * 请求用户同意数据收集
   * 返回 Promise，触发 UI 弹窗请求同意
   *
   * @returns {Promise<boolean>} 用户是否同意
   */
  requestConsent() {
    return new Promise((resolve) => {
      const consentEvent = new CustomEvent('analytics:requestconsent', {
        detail: { resolve }
      });
      window.dispatchEvent(consentEvent);
    });
  }

  /**
   * 设置用户同意状态
   * 设置同意状态，加载/卸载脚本
   *
   * @param {boolean} consent - 用户是否同意
   */
  setConsent(consent) {
    this.consentGiven = consent;
    this.enabled = consent;
    localStorage.setItem('fsm_analytics_consent', consent ? 'true' : 'false');

    if (consent) {
      this._initializeSession();
      this._loadAnalyticsScript();
    } else {
      this._unloadAnalyticsScript();
    }
  }

  /**
   * 追踪地图点选事件
   * 不发送精确经纬度，仅发送区域级别信息
   *
   * @param {Object} data - 点选数据
   * @param {number} data.lat - 纬度（会被模糊化）
   * @param {number} data.lng - 经度（会被模糊化）
   * @param {string} data.region - 区域名称
   * @param {string} data.node - 最近节点名称
   * @param {string} data.element - 五行属性
   */
  trackMapClick(data) {
    if (!this.enabled) return;

    const sanitizedData = this._sanitizeMapClickData(data);

    this._lastMapClick = {
      region: sanitizedData.region,
      element: sanitizedData.element,
      timestamp: Date.now()
    };

    this._sendEvent('map:click', sanitizedData);
  }

  /**
   * 追踪预约按钮点击
   *
   * @param {Object} data - 预约按钮数据
   * @param {string} data.region - 区域名称
   * @param {string} data.node - 最近节点名称
   */
  trackBookingClick(data) {
    if (!this.enabled) return;

    const sanitizedData = removePII(data);
    this._sendEvent('booking:click', sanitizedData);
  }

  /**
   * 追踪 Calendly 预约完成
   * 将预约数据与点选分析数据关联
   *
   * @param {Object} data - Calendly 预约数据
   * @param {string} data.scheduledAt - 预约时间
   * @param {string} data.eventType - 预约类型
   */
  trackBookingCompleted(data) {
    if (!this.enabled) return;

    const eventData = {
      ...removePII(data),
      sessionId: this.sessionId
    };

    if (this._lastMapClick) {
      eventData.lastMapClick = {
        region: this._lastMapClick.region,
        element: this._lastMapClick.element
      };
    }

    this._sendEvent('booking:completed', eventData);
  }

  /**
   * 获取当前同意状态
   * @returns {boolean}
   */
  hasConsent() {
    return this.consentGiven;
  }

  /**
   * 获取当前会话 ID
   * @returns {string|null}
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * 初始化匿名会话
   * @private
   */
  _initializeSession() {
    this.sessionId = getAnonymousSessionId();
  }

  /**
   * 清理地图点选数据
   * 模糊化坐标，移除 PII
   *
   * @param {Object} data - 原始点选数据
   * @returns {Object} 清理后的数据
   * @private
   */
  _sanitizeMapClickData(data) {
    if (!data) return {};

    const sanitized = { ...data };

    if (typeof data.lat === 'number' && typeof data.lng === 'number') {
      const obfuscated = obfuscateCoordinates(data.lat, data.lng);
      sanitized.lat = obfuscated.lat;
      sanitized.lng = obfuscated.lng;
    }

    return removePII(sanitized);
  }

  /**
   * 加载 Plausible 分析脚本
   * 预留接口，实际域名需配置后启用
   *
   * @private
   */
  _loadAnalyticsScript() {
    if (this.scriptLoaded) return;

    const plausibleDomain = import.meta.env?.VITE_PLAUSIBLE_DOMAIN;
    if (!plausibleDomain) {
      console.warn('Plausible domain not configured, analytics script not loaded');
      return;
    }

    if (document.querySelector('script[data-analytics="plausible"]')) {
      this.scriptLoaded = true;
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://plausible.io/js/script.js';
    script.dataset.domain = plausibleDomain;
    script.dataset.analytics = 'plausible';
    script.defer = true;

    script.onload = () => {
      this.scriptLoaded = true;
    };

    script.onerror = () => {
      console.error('Failed to load analytics script');
      this.scriptLoaded = false;
    };

    document.head.appendChild(script);
  }

  /**
   * 卸载分析脚本
   * 用户撤销同意时调用
   *
   * @private
   */
  _unloadAnalyticsScript() {
    const script = document.querySelector('script[data-analytics="plausible"]');
    if (script) {
      script.remove();
    }
    this.scriptLoaded = false;
  }

  /**
   * 发送事件到分析服务
   * 发送前进行 PDPA 合规检查
   *
   * @param {string} eventName - 事件名称
   * @param {Object} data - 事件数据
   * @private
   */
  _sendEvent(eventName, data) {
    if (!this.enabled) return;

    const checkResult = runPDPACheck(data);
    if (!checkResult.passed) {
      console.warn('PDPA check failed, event not sent:', checkResult.violations);
      return;
    }

    const eventData = {
      ...data,
      sessionId: this.sessionId,
      timestamp: Date.now()
    };

    if (window.plausible) {
      window.plausible(eventName, { props: eventData });
    }

    if (import.meta.env?.DEV) {
      console.log('[Analytics]', eventName, eventData);
    }
  }
}

export const analytics = new Analytics();
